import { Combobox } from '@base-ui/react/combobox'
import '../dropdown-field.css'
import '../variable-dropdown.css'
import { Popover } from '@base-ui/react/popover'
import { useVirtualizer } from '@tanstack/react-virtual'
import { CheckIcon, PlusIcon } from 'lucide-react'
import React, { useCallback, useContext, useRef, type Ref } from 'react'
import type { DropdownChoice } from '@companion-app/shared/Model/Common.js'
import { MenuPortalContext } from '../MenuPortalContext'

// A type rather than an interface, so that it satisfies base-ui's Group index signature
export type DropdownGroupBase = {
	id: string
	label: string
	items: DropdownChoice[]
}

/** Extended choice that can carry a display hint for the indicator slot. */
export type DropdownChoiceWithMeta = DropdownChoice & { plusIndicator?: boolean }

export interface DropdownInputPopupProps {
	noOptionsMessage?: string
	showIndicator?: boolean
	/** When true, unselected items show a not-allowed cursor (selection is rejected by onValueChange). */
	disableUnselected?: boolean
	/** When true, renders the list using @tanstack/react-virtual for large datasets. Only supports flat (non-grouped) item lists. */
	virtualized?: boolean
	/** When true, each item renders two lines: the id (top) and the label (bottom), matching the variable dropdown style. */
	fancyFormat?: boolean
}

export function DropdownInputPopup({
	noOptionsMessage,
	showIndicator,
	disableUnselected,
	virtualized,
	fancyFormat,
}: DropdownInputPopupProps): React.JSX.Element {
	const menuPortal = useContext(MenuPortalContext)

	const renderItem = (item: DropdownChoiceWithMeta) => (
		<Combobox.Item
			key={item.id}
			value={item.id}
			className={fancyFormat ? 'dropdown-field-item variable-dropdown-option' : 'dropdown-field-item'}
		>
			{showIndicator && (
				<span className="dropdown-field-item-indicator">
					{item.plusIndicator ? (
						<PlusIcon className="dropdown-field-item-indicator-icon dropdown-field-item-indicator-icon--always" />
					) : (
						<CheckIcon className="dropdown-field-item-indicator-icon" />
					)}
				</span>
			)}
			{fancyFormat ? (
				<div className="dropdown-field-item-content">
					<span className="var-name">{String(item.id)}</span>
					<span className="var-label">{item.label}</span>
				</div>
			) : (
				item.label
			)}
		</Combobox.Item>
	)

	return (
		<Combobox.Portal container={menuPortal ?? undefined}>
			<Combobox.Positioner className="dropdown-field-positioner" sideOffset={8}>
				<Combobox.Popup className="dropdown-field-popup">
					<Combobox.Empty>
						<div className="dropdown-field-empty">{noOptionsMessage || 'No options found.'}</div>
					</Combobox.Empty>
					{virtualized ? (
						<Combobox.List
							className={`dropdown-field-list dropdown-field-list--virtualized${disableUnselected ? ' dropdown-field-list--max-reached' : ''}`}
						>
							<VirtualizedComboboxList showIndicator={showIndicator} fancyFormat={fancyFormat} />
						</Combobox.List>
					) : (
						<Combobox.List
							className={`dropdown-field-list${disableUnselected ? ' dropdown-field-list--max-reached' : ''}`}
						>
							{(item: DropdownChoiceWithMeta | DropdownGroupBase) => {
								if ('items' in item) {
									return (
										<Combobox.Group key={item.id} items={item.items} className="dropdown-field-group">
											{item.label && (
												<Combobox.GroupLabel className="dropdown-field-group-label">{item.label}</Combobox.GroupLabel>
											)}
											<Combobox.Collection>{renderItem}</Combobox.Collection>
										</Combobox.Group>
									)
								}
								return renderItem(item)
							}}
						</Combobox.List>
					)}
				</Combobox.Popup>
			</Combobox.Positioner>
		</Combobox.Portal>
	)
}

interface VirtualComboboxItemProps {
	item: DropdownChoiceWithMeta
	index: number
	start: number
	totalCount: number
	measureElement: (el: Element | null) => void
	showIndicator?: boolean
	fancyFormat?: boolean
}

const VirtualComboboxItem = React.memo(function VirtualComboboxItem({
	item,
	index,
	start,
	totalCount,
	measureElement,
	showIndicator,
	fancyFormat,
}: VirtualComboboxItemProps): React.JSX.Element {
	return (
		<Combobox.Item
			value={item.id}
			index={index}
			data-index={index}
			ref={measureElement}
			aria-setsize={totalCount}
			aria-posinset={index + 1}
			className={fancyFormat ? 'dropdown-field-item variable-dropdown-option' : 'dropdown-field-item'}
			style={{
				position: 'absolute',
				top: 0,
				left: 0,
				width: '100%',
				transform: `translateY(${start}px)`,
			}}
		>
			{showIndicator && (
				<span className="dropdown-field-item-indicator">
					{item.plusIndicator ? (
						<PlusIcon className="dropdown-field-item-indicator-icon dropdown-field-item-indicator-icon--always" />
					) : (
						<CheckIcon className="dropdown-field-item-indicator-icon" />
					)}
				</span>
			)}
			{fancyFormat ? (
				<div className="dropdown-field-item-content">
					<span className="var-name">{String(item.id)}</span>
					<span className="var-label">{item.label}</span>
				</div>
			) : (
				item.label
			)}
		</Combobox.Item>
	)
})

interface VirtualizedListProps<T> {
	items: T[]
	renderItem: (
		item: T,
		index: number,
		start: number,
		totalCount: number,
		measureElement: (el: Element | null) => void
	) => React.ReactNode
}

function VirtualizedList<T>({ items, renderItem }: VirtualizedListProps<T>): React.JSX.Element | null {
	const scrollElementRef = useRef<HTMLDivElement | null>(null)

	const getScrollElement = useCallback(() => scrollElementRef.current, [])
	const estimateSize = useCallback(() => 37.6, [])

	// eslint-disable-next-line react-hooks/incompatible-library
	const virtualizer = useVirtualizer({
		count: items.length,
		getScrollElement,
		estimateSize,
		overscan: 20,
		paddingStart: 4,
		paddingEnd: 4,
		scrollPaddingStart: 4,
		scrollPaddingEnd: 4,
	})

	const scrollerRef: Ref<HTMLDivElement> = useCallback(
		(element: HTMLDivElement | null) => {
			scrollElementRef.current = element
			if (element) virtualizer.measure()
		},
		[virtualizer]
	)

	const totalSize = virtualizer.getTotalSize()

	if (!items.length) return null

	return (
		<div
			role="presentation"
			ref={scrollerRef}
			className="dropdown-field-list-scroller"
			style={{ '--total-size': `${totalSize}px` } as React.CSSProperties}
		>
			<div role="presentation" style={{ height: totalSize, width: '100%', position: 'relative' }}>
				{virtualizer.getVirtualItems().map((virtualRow) => {
					const item = items[virtualRow.index]
					if (!item) return null
					return (
						<React.Fragment key={virtualRow.key}>
							{renderItem(item, virtualRow.index, virtualRow.start, items.length, virtualizer.measureElement)}
						</React.Fragment>
					)
				})}
			</div>
		</div>
	)
}

function VirtualizedComboboxList({
	showIndicator,
	fancyFormat,
}: {
	showIndicator?: boolean
	fancyFormat?: boolean
}): React.JSX.Element | null {
	const filteredItems = Combobox.useFilteredItems<DropdownChoiceWithMeta>()

	return (
		<VirtualizedList
			items={filteredItems}
			renderItem={(item, index, start, totalCount, measureElement) => (
				<VirtualComboboxItem
					item={item}
					index={index}
					start={start}
					totalCount={totalCount}
					measureElement={measureElement}
					showIndicator={showIndicator}
					fancyFormat={fancyFormat}
				/>
			)}
		/>
	)
}

export interface VariableSuggestionPopupProps {
	open: boolean
	anchorRef: React.RefObject<HTMLElement | null>
	items: DropdownChoice[]
	focusedIndex: number
	onSelect: (item: DropdownChoice) => void
}

export function VariableSuggestionPopup({
	open,
	anchorRef,
	items,
	focusedIndex,
	onSelect,
}: VariableSuggestionPopupProps): React.JSX.Element {
	const menuPortal = useContext(MenuPortalContext)

	return (
		<Popover.Root
			open={open}
			onOpenChange={() => {
				// All state changes are controlled by the parent via the `open` prop
			}}
		>
			<Popover.Portal container={menuPortal ?? undefined}>
				<Popover.Positioner anchor={anchorRef} sideOffset={8} className="dropdown-field-positioner">
					<Popover.Popup
						className="dropdown-field-popup"
						initialFocus={false}
						finalFocus={false}
						onMouseDown={(e) => e.preventDefault()}
					>
						{items.length === 0 ? (
							<div className="dropdown-field-empty">No variables found.</div>
						) : (
							<div className="dropdown-field-list dropdown-field-list--virtualized">
								<VirtualizedList
									items={items}
									renderItem={(item, index, start, totalCount, measureElement) => (
										<div
											role="option"
											data-index={index}
											ref={(el) => measureElement(el)}
											aria-setsize={totalCount}
											aria-posinset={index + 1}
											data-highlighted={index === focusedIndex ? '' : undefined}
											className="dropdown-field-item variable-dropdown-option"
											style={{
												position: 'absolute',
												top: 0,
												left: 0,
												width: '100%',
												transform: `translateY(${start}px)`,
											}}
											onMouseDown={(e) => {
												e.preventDefault()
												onSelect(item)
											}}
										>
											<div className="dropdown-field-item-content">
												<span className="var-name">{String(item.id)}</span>
												<span className="var-label">{item.label}</span>
											</div>
										</div>
									)}
								/>
							</div>
						)}
					</Popover.Popup>
				</Popover.Positioner>
			</Popover.Portal>
		</Popover.Root>
	)
}
